import { useState, useEffect, useMemo } from 'react';
import { collection, addDoc, serverTimestamp, getDocs } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import Button from '../../components/ui/Button';
import { IconX } from '@tabler/icons-react';

const normalise = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

export default function AddModelModal({ makeId, makeName, category, categoryLabel, defaultSubCategory, subCategories, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [yearRange, setYearRange] = useState('');
  const [description, setDescription] = useState('');
  const [subCategory, setSubCategory] = useState(defaultSubCategory || '');
  const [existingModels, setExistingModels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, 'makes', makeId, 'models'));
      setExistingModels(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    })();
  }, [makeId]);

  const possibleMatches = useMemo(() => {
    if (!name || name.length < 2) return [];
    const normInput = normalise(name);
    return existingModels.filter(m => {
      const normExisting = normalise(m.name);
      if (normExisting === normInput) return true;
      if (normExisting.includes(normInput) || normInput.includes(normExisting)) return true;
      return false;
    });
  }, [name, existingModels]);

  const exactDuplicate = possibleMatches.some(m => normalise(m.name) === normalise(name));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (exactDuplicate) { setError(`A model named "${name}" already exists for ${makeName}.`); return; }
    if (!subCategory) { setError('Pick a sub-category for this model.'); return; }
    setError(''); setSaving(true);
    try {
      const docRef = await addDoc(collection(db, 'makes', makeId, 'models'), {
        name: name.trim(),
        normalisedName: normalise(name),
        yearRange: yearRange.trim() || null,
        description: description.trim() || null,
        category,
        subCategory,
        makeId,
        makeName,
        heroImageUrl: null,
        galleryImageUrls: [],
        operatorManualUrl: null,
        workshopManualUrl: null,
        specs: {},
        customFields: {},
        status: 'published',
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || null,
        createdByEmail: auth.currentUser?.email || null,
      });
      onCreated?.(docRef.id);
      onClose();
    } catch (err) {
      console.error(err);
      setError('Failed to create model: ' + err.message);
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={onClose}>
      <div style={{ width: 480, maxHeight: '90vh', overflow: 'auto', background: 'var(--color-bg-primary)', borderRadius: 14, boxShadow: 'var(--shadow-lg)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '0.5px solid var(--color-border-light)' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500 }}>New model</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{makeName} · {categoryLabel}</div>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)' }}><IconX size={18} stroke={1.5} /></div>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Model name *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. 6R 150" autoFocus required />
          </div>

          {possibleMatches.length > 0 && !exactDuplicate && (
            <div style={{ padding: '10px 12px', background: 'var(--color-info-light)', border: '0.5px solid #B8D4EB', borderRadius: 'var(--radius-md)', marginBottom: 16, fontSize: 12 }}>
              <div style={{ color: 'var(--color-info)', fontWeight: 500, marginBottom: 4 }}>Possible matches found</div>
              <div style={{ color: 'var(--color-text-secondary)' }}>Did you mean one of these existing models?</div>
              <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {possibleMatches.map(m => <span key={m.id} style={{ padding: '3px 8px', background: 'var(--color-bg-primary)', borderRadius: 'var(--radius-full)', fontSize: 11 }}>{m.name}</span>)}
              </div>
            </div>
          )}

          {exactDuplicate && (
            <div style={{ padding: '10px 12px', background: 'var(--color-danger-light)', borderRadius: 'var(--radius-md)', marginBottom: 16, fontSize: 12, color: 'var(--color-danger)' }}>
              "{name}" already exists under {makeName}.
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Sub-category *</label>
            {subCategories.length === 0 ? (
              <div style={{ padding: '10px 12px', background: 'var(--color-warning-light)', borderRadius: 'var(--radius-md)', fontSize: 12, color: 'var(--color-warning)' }}>
                No sub-categories defined for {categoryLabel}. Add them in Settings first.
              </div>
            ) : (
              <select value={subCategory} onChange={e => setSubCategory(e.target.value)} required>
                <option value="">Select a sub-category...</option>
                {subCategories.map(sc => (<option key={sc.id} value={sc.code}>{sc.name}</option>))}
              </select>
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Year range</label>
            <input type="text" value={yearRange} onChange={e => setYearRange(e.target.value)} placeholder="e.g. 2020–present, 2015–2022, 2018" />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief description of the model..." rows={3} style={{ resize: 'vertical', minHeight: 60 }} />
          </div>

          <div style={{ padding: '10px 12px', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', marginBottom: 16, fontSize: 12, color: 'var(--color-text-secondary)' }}>
            Once created, you'll be able to upload the hero image, manuals, specs and service schedule on the model page.
          </div>

          {error && <div style={{ padding: '8px 12px', background: 'var(--color-danger-light)', color: 'var(--color-danger)', borderRadius: 'var(--radius-md)', fontSize: 12, marginBottom: 16 }}>{error}</div>}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
            <Button type="submit" loading={saving} disabled={exactDuplicate || subCategories.length === 0}>Create model</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
